import { daysUntilExpirySydney, isExpiredInSydney } from '@l2u/shared/sydneyDates';
import { formatHuman, parseYmd } from './calendar.ts';
import type { SubmissionFile } from './types.ts';

export type ExpiryState = 'none' | 'ok' | 'expiring' | 'expired';

// Files within this many calendar days of `expires_at` are flagged as expiring.
export const EXPIRY_WARNING_DAYS = 14;

export function expiryState(
  file: Pick<SubmissionFile, 'expires_at'>,
  now: Date = new Date(),
): ExpiryState {
  if (!file.expires_at) return 'none';
  // Expiry is a calendar date in Australia/Sydney, not an instant: a document
  // dated 8/12 is valid through the whole of 8/12 and expired at 8/13 00:00.
  if (isExpiredInSydney(file.expires_at, now)) return 'expired';
  const days = daysUntilExpirySydney(file.expires_at, now);
  if (days === null) return 'none';
  return days <= EXPIRY_WARNING_DAYS ? 'expiring' : 'ok';
}

// The countdown line that sits under an expiry date. Pairs the state from
// expiryState() with the day count from daysUntilExpirySydney().
//
// `days` is whole Sydney calendar days, so it is 0 for the entire expiry date
// — the document is still valid that day, but "0 days remaining" reads as a
// bug. Worded to match the reminder email's own copy for the same case
// (renderDocumentExpiring in apps/api/src/lib/email.ts).
export function expiryCountdownLabel(state: ExpiryState, days: number): string {
  if (state === 'expired') return 'Expired';
  if (days <= 0) return 'Expires today';
  return `${days} day${days === 1 ? '' : 's'} remaining`;
}

// Convert an ISO timestamp string from the API to the `YYYY-MM-DD` form a
// `<input type="date">` accepts, or '' if absent.
export function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Convert a `YYYY-MM-DD` value back to an ISO timestamp at end-of-day UTC
// (so the file is considered valid through the whole calendar day).
export function fromDateInput(value: string): string | null {
  if (!value) return null;
  const parsed = new Date(`${value}T23:59:59.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

// Format a date-only value for display as "28 Oct 2026". `expires_at` is a
// *calendar date* the vendor picked, stored as an end-of-day-UTC timestamp (see
// fromDateInput). It must therefore be read in UTC too, so the day shown equals
// the day picked in every timezone — a plain toLocaleDateString() reads local
// time and, in UTC+ zones (e.g. Australia), rolls 23:59:59Z into the next
// calendar day. toDateInput reads the UTC parts; formatHuman is the DateField
// picker's own label, so the chip and the picker show the same text. (Intl is
// avoided on purpose: en-AU writes "June"/"Sept", and no locale means the
// browser's order, e.g. 10/28/2026.)
// NOTE: only for date-only values; real instants (signed_at, created_at, …)
// belong in local time and must NOT use this.
export function formatDateOnly(iso: string | null): string {
  const ymd = parseYmd(toDateInput(iso));
  return ymd ? formatHuman(ymd) : '';
}

/** "28 Oct 2026" for a real instant, on the viewer's LOCAL calendar day (formatDateOnly is UTC and for date-only values). '' for an invalid stamp. */
export function formatLocalDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatHuman({ year: d.getFullYear(), month: d.getMonth(), day: d.getDate() });
}
